import { useState, useEffect, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User,
  CreditCard,
  FileText,
  Building2,
  CheckCircle2,
  Upload,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Camera,
  X,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface OnboardingStep {
  key: "personal_info" | "id_upload" | "w9" | "bank_info";
  label: string;
  completed: boolean;
}

interface OnboardingStatus {
  onboardingCompleted: boolean;
  steps: OnboardingStep[];
  profile: Record<string, any>;
}

const stepIcons: Record<string, any> = {
  personal_info: User,
  id_upload: FileText,
  w9: CreditCard,
  bank_info: Building2,
};

export default function OnboardingGuard({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery<OnboardingStatus>({
    queryKey: ["/api/profile/onboarding-status"],
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/20 flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-4 w-64 mx-auto" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!data || data.onboardingCompleted) {
    return <>{children}</>;
  }

  return <OnboardingWizard status={data} />;
}

function OnboardingWizard({ status }: { status: OnboardingStatus }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const firstIncomplete = status.steps.findIndex((s) => !s.completed);
    return firstIncomplete >= 0 ? firstIncomplete : 0;
  });

  useEffect(() => {
    const firstIncomplete = status.steps.findIndex((s) => !s.completed);
    if (firstIncomplete >= 0 && status.steps[currentStepIndex]?.completed) {
      setCurrentStepIndex(firstIncomplete);
    }
  }, [status.steps]);

  const currentStep = status.steps[currentStepIndex];
  const completedCount = status.steps.filter((s) => s.completed).length;
  const progressPercent = (completedCount / status.steps.length) * 100;

  const submitStep = useMutation({
    mutationFn: async (payload: { step: string; data: Record<string, any> }) => {
      const res = await apiRequest("POST", "/api/profile/complete-step", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile/onboarding-status"] });
      toast({ title: "Step completed", description: `${currentStep?.label || 'Step'} saved successfully.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col">
      <div className="w-full bg-card border-b px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-bold"
              style={{ color: "hsl(12, 76%, 61%)" }}
              data-testid="text-app-name"
            >
              Staio
            </h1>
            <p className="text-muted-foreground text-sm mt-1" data-testid="text-setup-heading">
              Complete Your Setup
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout()}
            className="text-muted-foreground hover:text-foreground"
            data-testid="btn-onboarding-logout"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Sign Out
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-2xl space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span data-testid="text-progress-label">
                Step {currentStepIndex + 1} of {status.steps.length}
              </span>
              <span data-testid="text-progress-percent">{Math.round(progressPercent)}% complete</span>
            </div>
            <Progress value={progressPercent} className="h-2" data-testid="progress-bar" />

            <div className="flex items-center gap-2">
              {status.steps.map((step, i) => {
                const Icon = stepIcons[step.key] || User;
                const isActive = i === currentStepIndex;
                const isDone = step.completed;
                return (
                  <button
                    key={step.key}
                    onClick={() => setCurrentStepIndex(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-card border shadow-sm"
                        : isDone
                          ? "text-muted-foreground"
                          : "text-muted-foreground/60"
                    }`}
                    data-testid={`button-step-${step.key}`}
                  >
                    {isDone ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" data-testid="text-step-title">
                {(() => {
                  const Icon = stepIcons[currentStep.key] || User;
                  return <Icon className="w-5 h-5" />;
                })()}
                {currentStep.label}
                {currentStep.completed && (
                  <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentStep.key === "personal_info" && (
                <PersonalInfoForm
                  profile={status.profile}
                  onSubmit={(data) => submitStep.mutate({ step: "personal_info", data })}
                  isPending={submitStep.isPending}
                />
              )}
              {currentStep.key === "id_upload" && (
                <IdUploadForm
                  profile={status.profile}
                  onSubmit={(data) => submitStep.mutate({ step: "id_upload", data })}
                  isPending={submitStep.isPending}
                />
              )}
              {currentStep.key === "w9" && (
                <W9Form
                  profile={status.profile}
                  onSubmit={(data) => submitStep.mutate({ step: "w9", data })}
                  isPending={submitStep.isPending}
                />
              )}
              {currentStep.key === "bank_info" && (
                <BankInfoForm
                  profile={status.profile}
                  onSubmit={(data) => submitStep.mutate({ step: "bank_info", data })}
                  isPending={submitStep.isPending}
                />
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentStepIndex((i) => Math.max(0, i - 1))}
              disabled={currentStepIndex === 0}
              data-testid="button-prev-step"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setCurrentStepIndex((i) => Math.min(status.steps.length - 1, i + 1))
              }
              disabled={currentStepIndex === status.steps.length - 1}
              data-testid="button-next-step"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonalInfoForm({
  profile,
  onSubmit,
  isPending,
}: {
  profile: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({
    fullName: profile.fullName || "",
    phone: profile.phone || "",
    address: profile.address || "",
    city: profile.city || "",
    state: profile.state || "",
    zipCode: profile.zipCode || "",
    dateOfBirth: profile.dateOfBirth || "",
    emergencyContactName: profile.emergencyContactName || "",
    emergencyContactPhone: profile.emergencyContactPhone || "",
  });

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name</Label>
          <Input
            id="fullName"
            value={form.fullName}
            onChange={(e) => update("fullName", e.target.value)}
            required
            data-testid="input-fullName"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            required
            data-testid="input-phone"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <Input
          id="address"
          value={form.address}
          onChange={(e) => update("address", e.target.value)}
          required
          data-testid="input-address"
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            required
            data-testid="input-city"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            value={form.state}
            onChange={(e) => update("state", e.target.value)}
            required
            data-testid="input-state"
          />
        </div>
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label htmlFor="zipCode">Zip Code</Label>
          <Input
            id="zipCode"
            value={form.zipCode}
            onChange={(e) => update("zipCode", e.target.value)}
            required
            data-testid="input-zipCode"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="dateOfBirth">Date of Birth</Label>
        <Input
          id="dateOfBirth"
          type="date"
          value={form.dateOfBirth}
          onChange={(e) => update("dateOfBirth", e.target.value)}
          required
          data-testid="input-dateOfBirth"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="emergencyContactName">Emergency Contact Name</Label>
          <Input
            id="emergencyContactName"
            value={form.emergencyContactName}
            onChange={(e) => update("emergencyContactName", e.target.value)}
            required
            data-testid="input-emergencyContactName"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="emergencyContactPhone">Emergency Contact Phone</Label>
          <Input
            id="emergencyContactPhone"
            type="tel"
            value={form.emergencyContactPhone}
            onChange={(e) => update("emergencyContactPhone", e.target.value)}
            required
            data-testid="input-emergencyContactPhone"
          />
        </div>
      </div>
      <Button
        type="submit"
        className="w-full"
        disabled={isPending}
        style={{ backgroundColor: "hsl(12, 76%, 61%)" }}
        data-testid="button-submit-personal-info"
      >
        {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save & Continue
      </Button>
    </form>
  );
}

function IdUploadForm({
  profile,
  onSubmit,
  isPending,
}: {
  profile: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  isPending: boolean;
}) {
  const { toast } = useToast();
  const [idType, setIdType] = useState(profile.idType || "");
  const [idFrontUrl, setIdFrontUrl] = useState(profile.idFrontUrl || "");
  const [idBackUrl, setIdBackUrl] = useState(profile.idBackUrl || "");
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);

  const handleUpload = async (
    file: File,
    setUrl: (url: string) => void,
    setUploading: (v: boolean) => void
  ) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setUrl(data.url);
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idType || !idFrontUrl || !idBackUrl) {
      toast({
        title: "Missing fields",
        description: "Please select an ID type and upload both front and back photos.",
        variant: "destructive",
      });
      return;
    }
    onSubmit({ idType, idFrontUrl, idBackUrl });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>ID Type</Label>
        <Select value={idType} onValueChange={setIdType} data-testid="select-idType">
          <SelectTrigger data-testid="select-trigger-idType">
            <SelectValue placeholder="Select ID type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="drivers_license">Driver's License</SelectItem>
            <SelectItem value="passport">Passport</SelectItem>
            <SelectItem value="state_id">State ID</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Front Photo</Label>
          {idFrontUrl ? (
            <div className="relative">
              <img
                src={idFrontUrl}
                alt="ID Front"
                className="w-full h-40 object-cover rounded-md border"
                data-testid="img-id-front"
              />
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute top-1 right-1"
                onClick={() => setIdFrontUrl("")}
                data-testid="button-remove-id-front"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center cursor-pointer text-muted-foreground"
              onClick={() => document.getElementById("id-front-input")?.click()}
              data-testid="upload-area-id-front"
            >
              <input
                id="id-front-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file, setIdFrontUrl, setUploadingFront);
                  e.target.value = "";
                }}
                data-testid="input-id-front"
              />
              {uploadingFront ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <>
                  <Camera className="w-8 h-8 mb-2" />
                  <span className="text-sm">Upload Front</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Back Photo</Label>
          {idBackUrl ? (
            <div className="relative">
              <img
                src={idBackUrl}
                alt="ID Back"
                className="w-full h-40 object-cover rounded-md border"
                data-testid="img-id-back"
              />
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute top-1 right-1"
                onClick={() => setIdBackUrl("")}
                data-testid="button-remove-id-back"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center cursor-pointer text-muted-foreground"
              onClick={() => document.getElementById("id-back-input")?.click()}
              data-testid="upload-area-id-back"
            >
              <input
                id="id-back-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file, setIdBackUrl, setUploadingBack);
                  e.target.value = "";
                }}
                data-testid="input-id-back"
              />
              {uploadingBack ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <>
                  <Camera className="w-8 h-8 mb-2" />
                  <span className="text-sm">Upload Back</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isPending}
        style={{ backgroundColor: "hsl(12, 76%, 61%)" }}
        data-testid="button-submit-id-upload"
      >
        {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save & Continue
      </Button>
    </form>
  );
}

function W9Form({
  profile,
  onSubmit,
  isPending,
}: {
  profile: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  isPending: boolean;
}) {
  const [taxIdType, setTaxIdType] = useState<"ssn" | "ein">(
    profile.w9Ein ? "ein" : "ssn"
  );
  const [form, setForm] = useState({
    w9LegalName: profile.w9LegalName || "",
    w9BusinessName: profile.w9BusinessName || "",
    w9TaxClassification: profile.w9TaxClassification || "",
    w9Ssn: profile.w9Ssn || "",
    w9Ein: profile.w9Ein || "",
    w9Address: profile.w9Address || "",
    w9City: profile.w9City || "",
    w9State: profile.w9State || "",
    w9ZipCode: profile.w9ZipCode || "",
    w9ExemptPayeeCode: profile.w9ExemptPayeeCode || "",
    w9SignatureDate: new Date().toISOString().split("T")[0],
  });

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = { ...form };
    if (taxIdType === "ssn") {
      data.w9Ein = "";
    } else {
      data.w9Ssn = "";
    }
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-muted/50 border rounded-md p-3 text-sm text-muted-foreground" data-testid="text-w9-explanation">
        As an independent contractor (1099), we need your W-9 information for tax reporting purposes.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="w9LegalName">Legal Name</Label>
          <Input
            id="w9LegalName"
            value={form.w9LegalName}
            onChange={(e) => update("w9LegalName", e.target.value)}
            required
            data-testid="input-w9LegalName"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="w9BusinessName">Business Name (optional)</Label>
          <Input
            id="w9BusinessName"
            value={form.w9BusinessName}
            onChange={(e) => update("w9BusinessName", e.target.value)}
            data-testid="input-w9BusinessName"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tax Classification</Label>
        <Select
          value={form.w9TaxClassification}
          onValueChange={(v) => update("w9TaxClassification", v)}
        >
          <SelectTrigger data-testid="select-trigger-w9TaxClassification">
            <SelectValue placeholder="Select classification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
            <SelectItem value="llc_single">LLC (Single Member)</SelectItem>
            <SelectItem value="llc_partnership">LLC (Partnership)</SelectItem>
            <SelectItem value="c_corp">C Corporation</SelectItem>
            <SelectItem value="s_corp">S Corporation</SelectItem>
            <SelectItem value="trust">Trust/Estate</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <Label>Tax ID Type</Label>
        <RadioGroup
          value={taxIdType}
          onValueChange={(v) => setTaxIdType(v as "ssn" | "ein")}
          className="flex gap-4"
          data-testid="radio-tax-id-type"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="ssn" id="tax-ssn" data-testid="radio-ssn" />
            <Label htmlFor="tax-ssn" className="cursor-pointer">SSN</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="ein" id="tax-ein" data-testid="radio-ein" />
            <Label htmlFor="tax-ein" className="cursor-pointer">EIN</Label>
          </div>
        </RadioGroup>
        <Input
          placeholder={taxIdType === "ssn" ? "XXX-XX-XXXX" : "XX-XXXXXXX"}
          value={taxIdType === "ssn" ? form.w9Ssn : form.w9Ein}
          onChange={(e) =>
            update(taxIdType === "ssn" ? "w9Ssn" : "w9Ein", e.target.value)
          }
          required
          data-testid={`input-${taxIdType === "ssn" ? "w9Ssn" : "w9Ein"}`}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="w9Address">Address</Label>
        <Input
          id="w9Address"
          value={form.w9Address}
          onChange={(e) => update("w9Address", e.target.value)}
          required
          data-testid="input-w9Address"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="w9City">City</Label>
          <Input
            id="w9City"
            value={form.w9City}
            onChange={(e) => update("w9City", e.target.value)}
            required
            data-testid="input-w9City"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="w9State">State</Label>
          <Input
            id="w9State"
            value={form.w9State}
            onChange={(e) => update("w9State", e.target.value)}
            required
            data-testid="input-w9State"
          />
        </div>
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label htmlFor="w9ZipCode">Zip Code</Label>
          <Input
            id="w9ZipCode"
            value={form.w9ZipCode}
            onChange={(e) => update("w9ZipCode", e.target.value)}
            required
            data-testid="input-w9ZipCode"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="w9ExemptPayeeCode">Exempt Payee Code (optional)</Label>
          <Input
            id="w9ExemptPayeeCode"
            value={form.w9ExemptPayeeCode}
            onChange={(e) => update("w9ExemptPayeeCode", e.target.value)}
            data-testid="input-w9ExemptPayeeCode"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="w9SignatureDate">Signature Date</Label>
          <Input
            id="w9SignatureDate"
            type="date"
            value={form.w9SignatureDate}
            disabled
            data-testid="input-w9SignatureDate"
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isPending}
        style={{ backgroundColor: "hsl(12, 76%, 61%)" }}
        data-testid="button-submit-w9"
      >
        {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save & Continue
      </Button>
    </form>
  );
}

function BankInfoForm({
  profile,
  onSubmit,
  isPending,
}: {
  profile: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({
    bankName: profile.bankName || "",
    bankAccountNumber: profile.bankAccountNumber || "",
    bankRoutingNumber: profile.bankRoutingNumber || "",
    bankAccountType: profile.bankAccountType || "",
    zelleEmail: profile.zelleEmail || "",
    zellePhone: profile.zellePhone || "",
  });

  const update = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="bankName">Bank Name</Label>
        <Input
          id="bankName"
          value={form.bankName}
          onChange={(e) => update("bankName", e.target.value)}
          required
          data-testid="input-bankName"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bankAccountNumber">Account Number</Label>
          <Input
            id="bankAccountNumber"
            value={form.bankAccountNumber}
            onChange={(e) => update("bankAccountNumber", e.target.value)}
            required
            data-testid="input-bankAccountNumber"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bankRoutingNumber">Routing Number</Label>
          <Input
            id="bankRoutingNumber"
            value={form.bankRoutingNumber}
            onChange={(e) => update("bankRoutingNumber", e.target.value)}
            required
            data-testid="input-bankRoutingNumber"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Account Type</Label>
        <Select
          value={form.bankAccountType}
          onValueChange={(v) => update("bankAccountType", v)}
        >
          <SelectTrigger data-testid="select-trigger-bankAccountType">
            <SelectValue placeholder="Select account type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="checking">Checking</SelectItem>
            <SelectItem value="savings">Savings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="zelleEmail">Zelle Email</Label>
          <Input
            id="zelleEmail"
            type="email"
            value={form.zelleEmail}
            onChange={(e) => update("zelleEmail", e.target.value)}
            data-testid="input-zelleEmail"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="zellePhone">Zelle Phone</Label>
          <Input
            id="zellePhone"
            type="tel"
            value={form.zellePhone}
            onChange={(e) => update("zellePhone", e.target.value)}
            data-testid="input-zellePhone"
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isPending}
        style={{ backgroundColor: "hsl(12, 76%, 61%)" }}
        data-testid="button-submit-bank-info"
      >
        {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save & Continue
      </Button>
    </form>
  );
}
